import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '@/lib/utils';

const badgeVariants = cva(
  'inline-flex items-center justify-center rounded-full border font-medium w-fit whitespace-nowrap shrink-0 [&>svg]:size-3 gap-1 [&>svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive transition-[color,box-shadow] overflow-hidden',
  {
    variants: {
      variant: {
        default: 'border-transparent bg-primary text-primary-foreground [a&]:hover:bg-primary/90',
        secondary:
          'border-transparent bg-secondary text-secondary-foreground [a&]:hover:bg-secondary/90',
        destructive:
          'border-transparent bg-destructive text-white [a&]:hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60',
        outline: 'text-foreground [a&]:hover:bg-accent [a&]:hover:text-accent-foreground',
        primary: 'border-transparent bg-primary-100 text-primary-600 [a&]:hover:bg-primary-100/90',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

type BadgeColor =
  | 'green'
  | 'amber'
  | 'blue'
  | 'sky'
  | 'indigo'
  | 'violet'
  | 'slate'
  | 'orange'
  | 'rose'
  | 'red'
  | 'gray'
  | 'teal'
  | 'purple';

const badgeColorMap: Record<BadgeColor, string> = {
  green: 'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400',
  amber: 'bg-amber-100 text-amber-700 dark:bg-amber-900/20 dark:text-amber-400',
  blue: 'bg-blue-100 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400',
  sky: 'bg-sky-100 text-sky-700 dark:bg-sky-900/20 dark:text-sky-400',
  indigo: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/20 dark:text-indigo-400',
  violet: 'bg-violet-100 text-violet-700 dark:bg-violet-900/20 dark:text-violet-400',
  slate: 'bg-slate-100 text-slate-700 dark:bg-slate-900/20 dark:text-slate-400',
  orange: 'bg-orange-100 text-orange-700 dark:bg-orange-900/20 dark:text-orange-400',
  rose: 'bg-rose-100 text-rose-700 dark:bg-rose-900/20 dark:text-rose-400',
  red: 'bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-400',
  gray: 'bg-gray-100 text-gray-700 dark:bg-dark-gray-600 dark:text-dark-gray-200',
  teal: 'bg-teal-100 text-teal-700 dark:bg-teal-900/20 dark:text-teal-400',
  purple: 'bg-purple-100 text-purple-700 dark:bg-purple-900/20 dark:text-purple-400',
};

// Keys match `translatePaymentMethod`'s outputs so all billing reports share
// the same display strings.
const paymentMethodColors: Record<string, BadgeColor> = {
  Cash: 'green',
  Check: 'amber',
  'Check (Payor)': 'amber',
  'Credit Card': 'blue',
  CC: 'blue',
  'Credit Card (External)': 'blue',
  'CC (External)': 'blue',
  'CC (Payor)': 'blue',
  'Credit Transfer': 'indigo',
  'EFT - ACH': 'violet',
  ACH: 'violet',
  'Automated Clearing House (ACH)': 'violet',
  Wire: 'violet',
  BOP: 'slate',
  'Refund - Cash': 'rose',
  'Refund - Credit Card': 'rose',
  'Refund - Credit Card (External)': 'rose',
  'Refund - Check': 'rose',
  Insurance: 'teal',
};

const appointmentStatusColors: Record<string, BadgeColor> = {
  'Checked In': 'green',
  'Checked Out': 'blue',
  Scheduled: 'sky',
  Canceled: 'red',
  'No-Show': 'amber',
  Deleted: 'gray',
};

type BadgeSize = 'sm' | 'md';

const badgeSizeClasses: Record<BadgeSize, string> = {
  sm: 'px-1.5 py-0.5 text-[10px] leading-tight',
  md: 'px-2 py-0.5 text-xs',
};

function Badge({
  className,
  variant,
  color,
  size = 'md',
  asChild = false,
  ...props
}: React.ComponentProps<'span'> &
  VariantProps<typeof badgeVariants> & {
    asChild?: boolean;
    color?: BadgeColor;
    size?: BadgeSize;
  }) {
  const Comp = asChild ? Slot : 'span';

  const colorClasses = color ? badgeColorMap[color] : undefined;

  return (
    <Comp
      data-slot="badge"
      className={cn(
        colorClasses
          ? `inline-flex items-center justify-center rounded-full border border-transparent font-medium w-fit whitespace-nowrap shrink-0 [&>svg]:size-3 gap-1 [&>svg]:pointer-events-none transition-[color,box-shadow] overflow-hidden ${colorClasses}`
          : badgeVariants({ variant }),
        badgeSizeClasses[size],
        className
      )}
      {...props}
    />
  );
}

export { appointmentStatusColors, Badge, paymentMethodColors };
export type { BadgeColor, BadgeSize };
